import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * La mascota.
 *
 * Vive en el paquete y no en una aplicación porque la usan las dos: el panel
 * —los dos caminos de creación, el saludo del Resumen, el acceso— y el editor,
 * en el asistente de IA. Copiarla habría dado dos mascotas idénticas hasta la
 * primera corrección hecha en una sola.
 *
 * En el camino de proyecto en blanco sale en gris, que es lo que distingue
 * «esto lo armas tú» de «esto lo arma la inteligencia artificial» sin repetirlo
 * por escrito.
 *
 * Su hoja de estilos va aparte: `@webbuilder/ui/ai-mascot.css`.
 */
import { useEffect, useId, useRef } from 'react';
/**
 * Las cuatro formas del cuerpo, del hexágono al círculo.
 *
 * Todas llevan EXACTAMENTE los mismos comandos —seis pares de `L` y `C`— y por
 * tanto la misma cantidad de números en el mismo orden. Ésa es la condición que
 * permite pasar de una a otra interpolando cifra a cifra.
 *
 * Se interpolan en JavaScript y no con la propiedad CSS `d` porque esa
 * propiedad no está en todos los navegadores: donde falta, la animación no
 * ocurría —ni el latido ni el círculo al pulsar— y no había forma de notarlo
 * salvo mirándolo. Un `requestAnimationFrame` funciona en todos.
 */
const SHAPES = {
    /* Casi hexágono: esquinas apenas redondeadas, sin llegar al vértice vivo. */
    sharp: 'M111.15 53.33 C113.28 57.01 113.28 62.99 111.15 66.67 L91.35 100.96 C89.22 104.65 84.05 107.63 79.80 107.63 L40.20 107.63 C35.95 107.63 30.78 104.65 28.65 100.96 L8.85 66.67 C6.72 62.99 6.72 57.01 8.85 53.33 L28.65 19.04 C30.78 15.35 35.95 12.37 40.20 12.37 L79.80 12.37 C84.05 12.37 89.22 15.35 91.35 19.04 Z',
    /* Reposo. */
    base: 'M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z',
    /* Casi círculo, sin llegar a serlo. */
    round: 'M102.08 37.61 C109.21 49.98 109.21 70.02 102.08 82.39 L100.42 85.24 C93.29 97.61 75.93 107.63 61.65 107.63 L58.35 107.63 C44.07 107.63 26.71 97.61 19.58 85.24 L17.93 82.39 C10.79 70.02 10.79 49.98 17.92 37.61 L19.57 34.76 C26.71 22.39 44.07 12.37 58.35 12.37 L61.65 12.37 C75.93 12.37 93.29 22.39 100.42 34.76 Z',
    /* Círculo exacto: seis arcos de 60° sobre la misma circunferencia.
       Nunca se usa entero: es el destino hacia el que se redondea al pulsar, pero
       el recorrido se corta antes (`POP_ROUNDNESS`). Existe como referencia
       porque redondear «hacia un círculo» y quedarse a un 18% da una curvatura
       pareja en las seis esquinas; apuntar a una forma intermedia inventada, no. */
    circle: 'M107.63 32.50 C117.46 49.52 117.46 70.48 107.63 87.50 L107.63 87.50 C97.81 104.52 79.65 115.00 60.00 115.00 L60.00 115.00 C40.35 115.00 22.19 104.52 12.37 87.50 L12.37 87.50 C2.54 70.48 2.54 49.52 12.37 32.50 L12.37 32.50 C22.19 15.48 40.35 5.00 60.00 5.00 L60.00 5.00 C79.65 5.00 97.81 15.48 107.63 32.50 Z',
};
const SHAPE_NUMBER = /-?\d+(?:\.\d+)?/g;
/** Una forma intermedia entre dos, cifra a cifra. */
function blendShapes(from, to, t) {
    const target = to.match(SHAPE_NUMBER);
    if (!target)
        return from;
    let i = 0;
    return from.replace(SHAPE_NUMBER, (value) => {
        const end = Number(target[i++] ?? value);
        return (Number(value) + (end - Number(value)) * t).toFixed(2);
    });
}
/** Suaviza el vaivén: sin esto los extremos del latido se notan como topes. */
function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
/** Los cinco gestos del hover. El CSS tiene una animación por cada nombre. */
const REACTIONS = ['tilt', 'hop', 'spin', 'wink', 'scan'];
/** El del clic, que no entra en el sorteo: pulsar siempre hace lo mismo. */
const CLICK_REACTION = 'pop';
export function AiMascot({ tone = 'brand', cheer = 0, gaze, working = false, still = false }) {
    const mascotRef = useRef(null);
    /* En una referencia y no en las dependencias del efecto: cambiar a dónde mira
       no puede reiniciar el bucle de animación, que lleva su propio reloj —los
       episodios, el latido, la interpolación de la mirada— y volvería a empezar
       desde el reposo cada vez que apareciera o desapareciera un aviso. */
    const gazeRef = useRef(gaze);
    gazeRef.current = gaze;
    const workingRef = useRef(working);
    workingRef.current = working;
    /* Los ids del recorte y los degradados, únicos por mascota. Eran fijos, y con
       dos montadas a la vez —la pantalla de empezar sigue debajo del asistente—
       `url(#…)` apuntaba al recorte de la PRIMERA: el cuerpo de la de arriba se
       recortaba con la forma, el giro y el latido de la de abajo, y el filo no
       casaba con el relleno. */
    const uid = `cwa-mascot-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const ids = { clip: `${uid}-clip`, fill: `${uid}-fill`, shine: `${uid}-shine`, blur: `${uid}-blur` };
    useEffect(() => {
        const mascot = mascotRef.current;
        if (!mascot || still || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
            return;
        /* Los cuatro trazados que forman el cuerpo: el recorte, el filo de fuera,
           la luz de arriba y el filo oscuro. Se les escribe la MISMA `d` en el mismo
           fotograma; separarlos —con un `use`, o animando sólo uno— es lo que hacía
           que al girar o al pulsar el contorno se despegara del relleno. */
        const paths = [...mascot.querySelectorAll('.cwa-ai-mascot-path')];
        /* Dónde está el puntero, en coordenadas de la ventana. La dirección se
           calcula en el bucle y no aquí: hace falta la posición de la mascota, y
           leerla en cada movimiento del ratón es medir el diseño decenas de veces
           por segundo para un dato que sólo se usa una vez por fotograma. */
        let pointerX = 0;
        let pointerY = 0;
        let pointerSeen = false;
        let targetX = 0;
        let targetY = 0;
        let currentX = 0;
        let currentY = 0;
        /**
         * A cuántos cuerpos de distancia la mirada llega a su tope.
         *
         * Cuatro. Con menos, la mirada se clava en el extremo en cuanto el puntero
         * sale de encima y deja de responder; con muchos más, mover el ratón cerca
         * apenas mueve los ojos y parece que no mira nada.
         */
        const GAZE_REACH = 4;
        /* Cuándo se pulsó, para el inflado. `0` = no hay ninguno en curso. */
        let poppedAt = 0;
        /** Lo que dura el inflado al pulsar. */
        const POP_MS = 620;
        /* Hasta dónde se redondea al pulsar. Un gesto corto: acusa el toque sin
           convertirse en otra forma. */
        const POP_ROUNDNESS = 0.42;
        /* ── El cambio de esquinas, de vez en cuando ──────────────────────────
           En reposo la mascota se queda en su forma suave, ni afilada ni redonda.
           Cada tantos segundos se permite una excursión corta —afilarse o
           redondearse— y vuelve.
    
           Latiendo sin parar, el movimiento dejaba de significar nada: lo que se
           nota es el cambio, y para que haya cambio tiene que haber quietud. */
        const EPISODE_MS = 2600;
        const EPISODE_MIN_GAP = 7000;
        const EPISODE_EXTRA_GAP = 9000;
        let episodeAt = 0;
        let episodeFrom = 0;
        let episodeTarget = SHAPES.round;
        let lastShape = '';
        const scheduleEpisode = (now) => {
            episodeAt = now + EPISODE_MIN_GAP + Math.random() * EPISODE_EXTRA_GAP;
            // Una vez hacia la punta y otra hacia el círculo, al azar.
            episodeTarget = Math.random() < 0.5 ? SHAPES.sharp : SHAPES.round;
        };
        /** Escribe el trazado sólo si cambió: en reposo no hay nada que repintar. */
        const paint = (d) => {
            if (paths.length === 0 || d === lastShape)
                return;
            lastShape = d;
            for (const target of paths)
                target.setAttribute('d', d);
        };
        const drawShape = (now) => {
            /* El contador del inflado se libera SIEMPRE, haya trazado o no. Estando
               detrás del `return` de arriba, si el trazado faltara el gesto se
               quedaría marcado como en curso para siempre y no volvería a admitir
               otro clic. */
            if (poppedAt && now - poppedAt >= POP_MS)
                poppedAt = 0;
            if (paths.length === 0)
                return;
            if (poppedAt) {
                /* Va y vuelve dentro del mismo gesto: hasta la mitad se redondea, y de
                   ahí regresa al reposo. */
                const t = (now - poppedAt) / POP_MS;
                const swell = t < 0.5 ? easeInOut(t * 2) : easeInOut((1 - t) * 2);
                paint(blendShapes(SHAPES.base, SHAPES.circle, swell * POP_ROUNDNESS));
                return;
            }
            if (!episodeAt)
                scheduleEpisode(now);
            if (episodeFrom) {
                const t = (now - episodeFrom) / EPISODE_MS;
                if (t >= 1) {
                    episodeFrom = 0;
                    scheduleEpisode(now);
                }
                else {
                    const wave = t < 0.5 ? easeInOut(t * 2) : easeInOut((1 - t) * 2);
                    paint(blendShapes(SHAPES.base, episodeTarget, wave));
                    return;
                }
            }
            else if (now >= episodeAt) {
                episodeFrom = now;
            }
            paint(SHAPES.base);
        };
        let frame = window.requestAnimationFrame(function loop(now) {
            /* ── A dónde mira ─────────────────────────────────────────────────────
               A dónde está el puntero RESPECTO A ELLA, no respecto a la pantalla.
               Antes la ventana entera era el mapa: el centro de la pantalla era el
               frente, así que con la mascota a la izquierda —donde está, junto al
               saludo— poner el ratón justo a su derecha caía todavía en la mitad
               izquierda de la ventana y los ojos se iban al lado contrario. Miraba
               bien sólo por casualidad, cuando ella estaba centrada.
      
               Se toma la dirección exacta hacia el puntero y se le da fuerza según lo
               lejos que esté, hasta el tope. La caja se lee aquí, una vez por
               fotograma, porque la mascota se mueve —salta, gira— y su centro de hace
               un segundo ya no es el de ahora. */
            if (gazeRef.current === 'up') {
                /* Arriba del todo, y el puntero se queda sin voto. La transición hasta
                   ahí la hace la interpolación de abajo, así que levanta la vista en vez
                   de dar un salto. */
                targetX = 0;
                targetY = -1;
            }
            else if (pointerSeen) {
                const box = mascot.getBoundingClientRect();
                const dx = pointerX - (box.left + box.width / 2);
                const dy = pointerY - (box.top + box.height / 2);
                const distance = Math.hypot(dx, dy);
                if (distance < 0.5) {
                    /* Justo encima: no hay dirección que valga y dividir por casi cero
                       mandaría los ojos a dar un salto. Mira al frente. */
                    targetX = 0;
                    targetY = 0;
                }
                else {
                    const reach = Math.max(1, box.width * GAZE_REACH);
                    const strength = Math.min(1, distance / reach);
                    targetX = (dx / distance) * strength;
                    targetY = (dy / distance) * strength;
                }
            }
            currentX += (targetX - currentX) * 0.085;
            currentY += (targetY - currentY) * 0.085;
            /* Como fracción del cuerpo y no en píxeles: el recorrido de la mirada
               tiene que encoger con la mascota igual que los ojos. Nueve y seis
               centésimas del tamaño son los 9 y 6 píxeles de siempre a tamaño 100. */
            mascot.style.setProperty('--mascot-eye-x', `calc(var(--mascot-size) * ${(currentX * 0.09).toFixed(4)})`);
            mascot.style.setProperty('--mascot-eye-y', `calc(var(--mascot-size) * ${(currentY * 0.06).toFixed(4)})`);
            drawShape(now);
            frame = window.requestAnimationFrame(loop);
        });
        const followPointer = (event) => {
            pointerX = event.clientX;
            pointerY = event.clientY;
            pointerSeen = true;
        };
        /* Al pasar por encima reacciona, y no siempre igual.
           Cinco gestos cortos, uno al azar y sin repetir el anterior: una mascota
           que hace exactamente lo mismo cada vez deja de leerse como algo vivo a la
           segunda pasada. La clase se quita al terminar la animación, así que el
           siguiente hover vuelve a sortear. */
        let lastReaction = '';
        const react = () => {
            if (mascot.dataset.reaction || workingRef.current)
                return;
            const options = REACTIONS.filter((name) => name !== lastReaction);
            const pick = options[Math.floor(Math.random() * options.length)] ?? REACTIONS[0];
            lastReaction = pick;
            mascot.dataset.reaction = pick;
        };
        const endReaction = (event) => {
            // Sólo el gesto del cuerpo cierra el turno; los ojos y el parpadeo tienen
            // sus propias animaciones corriendo y las suyas no cuentan.
            if (event.animationName.startsWith('cwa-mascot-react-'))
                delete mascot.dataset.reaction;
        };
        /**
         * El clic infla la mascota. Uno cada vez.
         *
         * Reiniciándolo en cada pulsación, machacar el ratón dejaba la animación
         * cortada una y otra vez por su propio principio: la forma daba tirones y
         * el crecimiento nunca llegaba a completarse. Lo que se veía no era una
         * mascota reaccionando cinco veces, era una parpadeando.
         *
         * Mientras hay uno en marcha los demás se ignoran. Dura 620 ms —menos de lo
         * que se tarda en volver a apuntar y pulsar— así que no se siente como que
         * el clic se pierde, sino como que el gesto se toma su tiempo.
         */
        const pop = (event) => {
            if (poppedAt || workingRef.current)
                return;
            poppedAt = event.timeStamp;
            delete mascot.dataset.reaction;
            // Un fotograma de por medio para que el navegador dé por terminada la
            // animación anterior; sin esto, reasignar el mismo atributo no la
            // reinicia y el gesto no arrancaría.
            window.requestAnimationFrame(() => { mascot.dataset.reaction = CLICK_REACTION; });
        };
        mascot.addEventListener('pointerenter', react);
        mascot.addEventListener('click', pop);
        mascot.addEventListener('animationend', endReaction);
        window.addEventListener('pointermove', followPointer, { passive: true });
        return () => {
            mascot.removeEventListener('pointerenter', react);
            mascot.removeEventListener('click', pop);
            mascot.removeEventListener('animationend', endReaction);
            window.removeEventListener('pointermove', followPointer);
            if (frame)
                window.cancelAnimationFrame(frame);
        };
    }, [still]);
    /* El gesto de contenta. Vive en su propio efecto y no dentro del grande porque
       ése se monta una sola vez —lleva los oyentes y el bucle de dibujo— y esto
       tiene que volver a dispararse cada vez que se lo piden.
  
       El fotograma de por medio es el mismo truco que el clic: reasignar el mismo
       atributo no reinicia una animación en curso. */
    useEffect(() => {
        const mascot = mascotRef.current;
        if (!mascot || !cheer || still)
            return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
            return;
        delete mascot.dataset.reaction;
        const frame = window.requestAnimationFrame(() => { mascot.dataset.reaction = 'cheer'; });
        return () => window.cancelAnimationFrame(frame);
    }, [cheer, still]);
    return (_jsxs("span", { className: `cwa-ai-mascot is-${tone}${gaze === 'up' ? ' is-waiting' : ''}${working ? ' is-working' : ''}${still ? ' is-still' : ''}`, ref: mascotRef, "aria-hidden": "true", children: [_jsxs("svg", { className: "cwa-ai-mascot-shape", viewBox: "0 0 120 120", focusable: "false", children: [_jsxs("defs", { children: [_jsx("clipPath", { id: ids.clip, children: _jsx("path", { className: "cwa-ai-mascot-path", d: "M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z" }) }), _jsxs("linearGradient", { id: ids.fill, x1: "60", y1: "0", x2: "60", y2: "120", gradientUnits: "userSpaceOnUse", children: [_jsx("stop", { className: "cwa-mascot-stop-top" }), _jsx("stop", { className: "cwa-mascot-stop-mid", offset: "0.42" }), _jsx("stop", { className: "cwa-mascot-stop-bottom", offset: "1" })] }), _jsxs("radialGradient", { id: ids.shine, cx: "60", cy: "8", r: "66", gradientUnits: "userSpaceOnUse", children: [_jsx("stop", { stopColor: "#ffffff", stopOpacity: "0.5" }), _jsx("stop", { offset: "1", stopColor: "#ffffff", stopOpacity: "0" })] }), _jsx("filter", { id: ids.blur, x: "-30%", y: "-30%", width: "160%", height: "160%", children: _jsx("feGaussianBlur", { stdDeviation: "0.9" }) })] }), _jsx("path", { className: "cwa-ai-mascot-path cwa-ai-mascot-rim", fill: "none", strokeWidth: "3", d: "M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z" }), _jsxs("g", { clipPath: `url(#${ids.clip})`, children: [_jsx("rect", { x: "-6", y: "-6", width: "132", height: "132", fill: `url(#${ids.fill})` }), _jsx("rect", { x: "-6", y: "-6", width: "132", height: "132", fill: `url(#${ids.shine})` }), _jsx("g", { transform: "translate(0 1.4)", children: _jsx("path", { className: "cwa-ai-mascot-path", fill: "none", stroke: "#ffffff", strokeWidth: "2", opacity: "0.42", filter: `url(#${ids.blur})`, d: "M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z" }) }), _jsx("path", { className: "cwa-ai-mascot-path cwa-ai-mascot-edge", fill: "none", strokeWidth: "1.4", opacity: "0.34", d: "M106.75 45.71 C111.31 53.60 111.31 66.40 106.75 74.29 L95.75 93.34 C91.19 101.23 80.11 107.63 71.00 107.63 L49.00 107.63 C39.89 107.63 28.81 101.23 24.25 93.34 L13.25 74.29 C8.69 66.40 8.69 53.60 13.25 45.71 L24.25 26.66 C28.81 18.77 39.89 12.37 49.00 12.37 L71.00 12.37 C80.11 12.37 91.19 18.77 95.75 26.66 Z" })] })] }), _jsx("span", { className: "cwa-ai-mascot-eyes", children: _jsxs("span", { className: "cwa-ai-mascot-blink", children: [_jsx("i", {}), _jsx("i", {})] }) })] }));
}
//# sourceMappingURL=AiMascot.js.map